import React, { useState, useRef, useEffect } from "react";
import { AsyncTypeahead } from "react-bootstrap-typeahead";
import { UseFormRegister, UseFormSetValue } from "react-hook-form";
import { useLazySearchSoldToQuery, SoldToSearchResult } from "common/services/QQApi";
import { FormFields } from "../utils";
import "./ShipToTypeAhead.scss";
import { datadogLogs } from "@datadog/browser-logs";

interface SoldToTypeAheadProps {
  register: UseFormRegister<FormFields>;
  setValue: UseFormSetValue<FormFields>;
  defaultValue?: string;
  token: string;
  onSoldToChange?: (soldToId: string, soldToName: string, displayValue: string) => void;
}

interface SoldToOption {
  soldToId: string;
  soldToName: string;
}

let persistedSelected: SoldToOption[] = [];

export const SoldToTypeAhead: React.FunctionComponent<SoldToTypeAheadProps> = ({
  register,
  setValue,
  defaultValue = "",
  token,
  onSoldToChange = () => {},
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [options, setOptions] = useState<SoldToOption[]>([]);
  const [selected, setSelected] = useState<SoldToOption[]>(persistedSelected);
  const [triggerSearch] = useLazySearchSoldToQuery();
  const typeaheadRef = useRef<any>(null);
  const initializedRef = useRef(false);

  const { onBlur } = register("soldTo", {
    required: true,
  });

  useEffect(() => {
    if (initializedRef.current) {
      return;
    }

    initializedRef.current = true;

    if (persistedSelected.length > 0) {
      const picked = persistedSelected[0];
      const soldToDisplay = `${picked.soldToId} - ${picked.soldToName}`;

      setValue("soldTo", soldToDisplay, { shouldValidate: false, shouldDirty: false });
      setValue("soldToId", picked.soldToId, { shouldValidate: false, shouldDirty: false });
      setValue("soldToName", picked.soldToName, { shouldValidate: false, shouldDirty: false });
      onSoldToChange(picked.soldToId, picked.soldToName, soldToDisplay);
      return;
    }

    if (!defaultValue) {
      return;
    }

    const match = defaultValue.match(/^([^\s-]+)\s*-\s*(.+)$/);
    if (match) {
      const [, id, name] = match;

      const option = { soldToId: id, soldToName: name };
      setSelected([option]);
      persistedSelected = [option];

      setValue("soldTo", defaultValue, { shouldValidate: false, shouldDirty: false });
      setValue("soldToId", id, { shouldValidate: false, shouldDirty: false });
      setValue("soldToName", name, { shouldValidate: false, shouldDirty: false });
      onSoldToChange(id, name, defaultValue);
    }
  }, [defaultValue, onSoldToChange, setValue]);

  const handleSoldToSearch = async (query: string) => {
    console.log("[SoldToTypeAhead] Search triggered with query:", query);

    try {
      setIsLoading(true);
      const result = await triggerSearch({ token, name: query }).unwrap();

      const nextOptions = result.map((item: SoldToSearchResult) => ({
        soldToId: item.soldToId,
        soldToName: item.soldToName,
      }));

      setOptions(nextOptions);
    } catch (error) {
      console.error("[SoldToTypeAhead] Search error:", error);
      datadogLogs.logger.error("[Sold To TypeAhead]: Error with sold-to search", {
        name: "Error: Sold-to search",
        details: error,
      });
      setOptions([]);
    } finally {
      setIsLoading(false);
    }
  };

  const onChange = (selectedOptions: SoldToOption[]) => {
    setSelected(selectedOptions);
    persistedSelected = selectedOptions; // Persist outside component

    if (selectedOptions.length > 0) {
      const picked = selectedOptions[0];
      const soldToDisplay = `${picked.soldToId} - ${picked.soldToName}`;
      setValue("soldTo", soldToDisplay, { shouldValidate: true, shouldDirty: true });
      setValue("soldToId", picked.soldToId, { shouldValidate: false, shouldDirty: true });
      setValue("soldToName", picked.soldToName, { shouldValidate: false, shouldDirty: true });
      onSoldToChange(picked.soldToId, picked.soldToName, soldToDisplay);
    } else {
      setValue("soldTo", "", { shouldValidate: true, shouldDirty: true });
      setValue("soldToId", "", { shouldValidate: false, shouldDirty: true });
      setValue("soldToName", "", { shouldValidate: false, shouldDirty: true });
      onSoldToChange("", "", "");
    }
  };

  return (
    <AsyncTypeahead
      id="sold-to"
      ref={typeaheadRef}
      isLoading={isLoading}
      selected={selected}
      clearButton={false}
      inputProps={{ autoComplete: "off" }}
      delay={1000}
      placeholder="Enter Sold To name..."
      searchText="Searching"
      minLength={3}
      useCache={true}
      onSearch={handleSoldToSearch}
      options={options}
      labelKey={(option: SoldToOption) => `${option.soldToId} - ${option.soldToName}`}
      onChange={onChange}
      onBlur={onBlur}
      renderMenuItemChildren={(option: SoldToOption) => (
        <span>{`${option.soldToId} - ${option.soldToName}`}</span>
      )}
    />
  );
};
